import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import apiClient from "../../AppClient";
import AlertModal from "../../Components/AlertModal";
import { useAuth } from "../../context/AuthContext";
import { Table, Card, Button, Form, InputGroup, Container, Row, Col } from "react-bootstrap";
import { FaSearch, FaUserPlus, FaEdit, FaTrash, FaEye } from "react-icons/fa";

const StaffListScreen = () => {
  const { permissions } = useAuth();
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [searchTerm, setSearchTerm] = useState("");

  const rowsPerPage = 4;

  const fetchUsers = () => {
    setLoading(true);
    apiClient
      .get("/staff/")
      .then((response) => {
        setUsers(response.data.staff);
        setLoading(false);
      })
      .catch((error) => {
        console.error("dd", error.response.data.detail);
        setError(error.response.data.detail);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDelete = (id) => {
    apiClient
      .delete(`/staff/${id}/delete/`)
      .then((response) => {
        fetchUsers();
      })
      .catch((error) => console.error(error));
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) || 
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic for filtered users
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">جاري التحميل...</span>
      </div>
    </div>
  );

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2 className="mb-3 ">قائمة المستخدمين</h2>
          <Card className="shadow-sm">
            <Card.Body>
              <Row className="mb-3 align-items-center">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="ابحث باسم المستخدم أو البريد الإلكتروني..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </Col>
                <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0">
                  <Button 
                    variant="success" 
                    onClick={() => navigate("/staff/add")}
                    className="d-flex align-items-center"
                  >
                    <FaUserPlus className="me-2" /> إضافة مستخدم جديد
                  </Button>
                </Col>
              </Row>

              {error && <div className="alert alert-danger">{error}</div>}

              {filteredUsers.length === 0 ? (
                <div className="text-center py-5">
                  <h5 className="text-muted">لا توجد نتائج مطابقة للبحث</h5>
                </div>
              ) : isMobile ? (
                // Mobile View: Use Cards for Each User
                <div>
                  {paginatedUsers.map((user) => (
                    <Card key={user.id} className="mb-3 border-0 shadow-sm">
                      <Card.Body>
                        <Card.Title className="text-primary">{user.username}</Card.Title>
                        <Card.Text>
                          <strong>البريد الإلكتروني:</strong> {user.email}
                          <br />
                          <strong>تاريخ الانضمام:</strong> {user.date_joined}
                        </Card.Text>
                        <div className="d-flex justify-content-between">
                          <Link to={`/staff/${user.id}`} className="btn btn-outline-primary">
                            <FaEye className="me-1" /> عرض
                          </Link>
                          <Button
                            variant="outline-warning"
                            onClick={() => navigate(`/staff/${user.id}/edit`)}
                          >
                            <FaEdit className="me-1" /> تعديل
                          </Button>
                          <AlertModal
                            myaction={() => handleDelete(user.id)}
                            customebutton={
                              <Button variant="outline-danger">
                                <FaTrash className="me-1" /> حذف
                              </Button>
                            }
                            message="هل تريد حذف هذا المستخدم؟"
                            title="تأكيد الحذف"
                            color="danger"
                            savetitle="نعم"
                          />
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>
              ) : (
                // Desktop View: Use Table
                <div className="mainTable">
                  <Table responsive bordered hover className="d-md-table">
                    <thead className="table-primary">
                      <tr>
                        <th>اسم المستخدم</th>
                        <th>البريد الإلكتروني</th>
                        <th>تاريخ الانضمام</th>
                        <th>إجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user) => (
                        <tr key={user.id}>
                          <td className="align-middle">{user.username}</td>
                          <td className="align-middle">{user.email}</td>
                          <td className="align-middle">{user.date_joined}</td>
                          <td>
                            <Link
                              to={`/staff/${user.id}`}
                              className="btn btn-sm btn-outline-primary mx-1"
                            >
                              <FaEye className="me-1" /> عرض
                            </Link>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              className="mx-1"
                              onClick={() => navigate(`/staff/${user.id}/edit`)}
                            >
                              <FaEdit className="me-1" /> تعديل
                            </Button>
                            <AlertModal
                              myaction={() => handleDelete(user.id)}
                              customebutton={
                                <Button variant="outline-danger" size="sm" className="mx-1">
                                  <FaTrash className="me-1" /> حذف
                                </Button>
                              }
                              message="هل تريد حذف هذا المستخدم؟"
                              title="تأكيد الحذف"
                              color="danger"
                              savetitle="نعم"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Pagination Controls */}
              {filteredUsers.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-4">
                  <Button
                    variant="outline-primary"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    الصفحة السابقة
                  </Button>
                  <span className="badge bg-primary fs-6">
                    الصفحة {currentPage} من {totalPages || 1}
                  </span>
                  <Button
                    variant="outline-primary"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    الصفحة التالية
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default StaffListScreen;